import React from 'react';
import { 
  Cloud, 
  Database, 
  Terminal, 
  Code, 
  Server, 
  Shield, 
  Workflow, 
  BarChart2
} from 'lucide-react';

const Skills = () => {
  const skillCategories = [
    {
      title: "Cloud Platforms",
      icon: <Cloud className="text-blue-600" />,
      skills: [
        {
          name: "Amazon Web Services (AWS)",
          proficiency: 90,
          items: ["EC2", "S3", "Lambda", "EKS", "CloudFront", "API Gateway", "Cognito", "IAM", "CloudFormation", "CodePipeline"]
        },
        {
          name: "Google Cloud Platform (GCP)",
          proficiency: 85,
          items: ["Compute Engine", "GKE", "BigQuery", "Dataproc", "IAM", "CloudBuild", "Key Management"]
        },
        {
          name: "Microsoft Azure",
          proficiency: 55,
          items: ["Virtual Machines", "AKS", "ActiveDirectory", "Azure Database", "Blob Storage"]
        }
      ]
    },
    {
      title: "Infrastructure as Code",
      icon: <Terminal className="text-green-600" />,
      skills: [
        {
          name: "Terraform",
          proficiency: 95,
          items: ["Multi-cloud infrastructure", "Modular configuration", "State management", "CI/CD integration"]
        },
        {
          name: "CloudFormation",
          proficiency: 80,
          items: ["Nested stacks", "Dynamic references", "Custom resources"]
        },
        {
          name: "AWS SAM",
          proficiency: 85,
          items: ["Serverless applications", "Lambda deployments", "API integrations"]
        }
      ]
    },
    {
      title: "Container & Orchestration",
      icon: <Workflow className="text-purple-600" />,
      skills: [
        {
          name: "Kubernetes",
          proficiency: 85,
          items: ["Cluster management", "Self-managed deployments", "Workload optimization", "RBAC configuration"]
        },
        {
          name: "Docker",
          proficiency: 90,
          items: ["Container optimization", "Multi-stage builds", "Custom images"]
        }
      ]
    },
    {
      title: "Data Engineering",
      icon: <Database className="text-red-600" />,
      skills: [
        {
          name: "Spark",
          proficiency: 80,
          items: ["Data processing", "Cluster management", "Performance tuning"]
        },
        {
          name: "Hadoop",
          proficiency: 75,
          items: ["HDFS", "MapReduce", "YARN"]
        },
        {
          name: "Airflow",
          proficiency: 85,
          items: ["Workflow orchestration", "DAG management", "Custom operators"]
        },
        {
          name: "Data Warehousing",
          proficiency: 80,
          items: ["BigQuery", "Data modeling", "ETL pipelines"]
        }
      ]
    },
    {
      title: "DevOps & CI/CD",
      icon: <Server className="text-indigo-600" />,
      skills: [
        {
          name: "Deployment Automation",
          proficiency: 90,
          items: ["Pipeline design", "Environment configuration", "Artifact management"]
        },
        {
          name: "Infrastructure Monitoring",
          proficiency: 85,
          items: ["Metrics collection", "Alerting", "Dashboard creation"]
        },
        {
          name: "Version Control",
          proficiency: 95,
          items: ["Git", "Branching strategies", "Code review workflows"]
        }
      ]
    },
    {
      title: "Security",
      icon: <Shield className="text-yellow-600" />,
      skills: [
        {
          name: "IAM & Access Control",
          proficiency: 90,
          items: ["Role-based access", "Least privilege principle", "Multi-factor authentication"]
        },
        {
          name: "Security Scanning",
          proficiency: 85,
          items: ["Fortify", "WebInspect", "DbProtect", "Vulnerability remediation"]
        },
        {
          name: "Encryption & Key Management",
          proficiency: 80,
          items: ["KMS", "Certificate management", "Data protection"]
        }
      ]
    },
    {
      title: "Frontend Development",
      icon: <Code className="text-pink-600" />,
      skills: [
        {
          name: "React",
          proficiency: 80,
          items: ["Component architecture", "State management", "Performance optimization"]
        },
        {
          name: "JavaScript/TypeScript",
          proficiency: 85,
          items: ["ES6+", "Async programming", "Type safety"]
        },
        {
          name: "Modern CSS",
          proficiency: 80,
          items: ["Tailwind CSS", "Flexbox", "Grid", "Responsive design"]
        }
      ]
    },
    {
      title: "Data Visualization",
      icon: <BarChart2 className="text-orange-600" />,
      skills: [
        {
          name: "Visualization Tools",
          proficiency: 75,
          items: ["Dashboard design", "Interactive visualizations", "Data storytelling"]
        },
        {
          name: "API Design",
          proficiency: 85,
          items: ["RESTful patterns", "Authentication integration", "Performance optimization"]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-16">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Technical Skills</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            My expertise spans cloud infrastructure, data engineering, and DevOps with a focus on 
            building scalable, secure, and automated systems.
          </p>
        </div>

        <div className="space-y-16">
          {skillCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center">
                  <div className="p-3 bg-gray-100 rounded-full mr-4">
                    {category.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">{category.title}</h2>
                </div>
              </div>

              <div className="p-6">
                {category.skills.map((skill, skillIndex) => (
                  <div key={skillIndex} className="mb-8 last:mb-0">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-800">{skill.name}</h3>
                      <span className="text-sm text-gray-600 font-medium">{skill.proficiency}%</span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
                      <div 
                        className="bg-blue-600 h-2.5 rounded-full" 
                        style={{ width: `${skill.proficiency}%` }}
                      ></div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {skill.items.map((item, itemIndex) => (
                        <span 
                          key={itemIndex}
                          className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Professional Development</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            I'm constantly learning and expanding my skillset through hands-on projects, 
            professional courses, and contributing to the tech community. Currently, I'm focusing 
            on enhancing my expertise in multi-cloud architectures and advanced data engineering.
          </p>
          
          <div className="inline-flex space-x-4">
            <a 
              href="/contact" 
              className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-blue-700 transition duration-300"
            >
              Let's Connect
            </a>
            <a 
              href="/projects" 
              className="bg-white border border-gray-300 text-gray-700 font-semibold px-6 py-3 rounded-lg shadow-lg hover:bg-gray-50 transition duration-300"
            >
              View My Projects
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Skills;